import {
  Alert,
  AlertIcon,
  Box,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  ButtonGroup,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Select,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import ContentCard from "../nav/ContentCard";
import { useState, useEffect, useRef } from "react";
import { useApiService } from "../../redux-action/api.service";
import { Score } from "../../types/score-types";
import { CertificateModal } from "../spelling/IQScore/CertificateModal";
import { ScoreCard } from "../spelling/IQScore/ScoreCard";
import { motion } from "framer-motion";
import { SearchIcon, DeleteIcon } from "@chakra-ui/icons";

const MotionBox = motion(Box);

export const ManageCharactersResultsDashboard = () => {
  const api = useApiService();
  const [scores, setScores] = useState<Score[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [playerResult, setPlayerResult] = useState<Score>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 20;
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [scoreToDelete, setScoreToDelete] = useState<Score | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();

  useEffect(() => {
    const fetchScores = async () => {
      try {
        setIsLoading(true);
        const response = await api.service(`game-scores/all-scores`).find();
        setScores(response);
      } catch (error) {
        setError(
          new Error("Failed to load your scores. Please try again later."),
        );
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteClick = (score: Score) => {
    setScoreToDelete(score);
  };

  const handleDeleteConfirm = async () => {
    if (!scoreToDelete?.id) return;

    try {
      await api.service(`game-scores`).delete(scoreToDelete.id);
      setScores(scores.filter((score) => score.id !== scoreToDelete.id));

      toast({
        title: "Score deleted",
        description: "The score has been successfully deleted.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete the score. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error deleting score:", error);
    } finally {
      setScoreToDelete(null);
    }
  };

  // Calculate statistics
  const calculateStatistics = () => {
    if (!scores.length)
      return {
        totalScores: 0,
        averageScore: 0,
        highestScore: 0,
        lowestScore: 0,
        averageMemoryIQ: 0,
        recentTests: 0,
      };

    const totalScores = scores.length;
    const averageScore = Math.round(
      scores.reduce((acc, curr) => acc + curr.score, 0) / totalScores,
    );
    const highestScore = Math.max(...scores.map((s) => s.score));
    const lowestScore = Math.min(...scores.map((s) => s.score));
    const averageMemoryIQ = Math.round(
      scores.reduce((acc, curr) => acc + curr.memoryIQScore, 0) / totalScores,
    );

    // Tests taken in last 24 hours
    const last24Hours = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const recentTests = scores.filter(
      (s) => new Date(s.createdAt || "") > last24Hours,
    ).length;

    return {
      totalScores,
      averageScore,
      highestScore,
      lowestScore,
      averageMemoryIQ,
      recentTests,
    };
  };

  const stats = calculateStatistics();

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // Filter scores based on search criteria
  const getFilteredScores = () => {
    return scores.filter((score) => {
      const searchTerm = searchQuery.toLowerCase();

      // Handle score range filtering
      if (scoreFilter !== "all") {
        const numScore = score.score;
        switch (scoreFilter) {
          case "90+":
            if (numScore < 90) return false;
            break;
          case "70-89":
            if (numScore < 70 || numScore >= 90) return false;
            break;
          case "<70":
            if (numScore >= 70) return false;
            break;
        }
      }

      // Handle text search
      if (!searchQuery) return true;

      switch (searchField) {
        case "name":
          return `${score.firstName} ${score.lastName}`
            .toLowerCase()
            .includes(searchTerm);
        case "email":
          return score.email.toLowerCase().includes(searchTerm);
        case "country":
          return score.country.toLowerCase().includes(searchTerm);
        case "all":
          return (
            `${score.firstName} ${score.lastName}`
              .toLowerCase()
              .includes(searchTerm) ||
            score.email.toLowerCase().includes(searchTerm) ||
            score.country.toLowerCase().includes(searchTerm)
          );
        default:
          return true;
      }
    });
  };

  const filteredScores = getFilteredScores();
  const currentItems = filteredScores.slice(indexOfFirstItem, indexOfLastItem);

  // Update pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, searchField, scoreFilter]);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="50vh">
        <Spinner size="xl" color="blue.500" thickness="4px" />
      </Flex>
    );
  }
  return (
    <ContentCard>
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8}>
          {/* Header Section */}
          <Box textAlign="center" w="full">
            <Heading as="h1" size="2xl" mb={4} letterSpacing="tight">
              Manage Characters Results Dashboard
            </Heading>
          </Box>

          {/* Statistics Cards */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} w="full">
            <Stat
              p={6}
              shadow="md"
              border="1px"
              borderColor="gray.200"
              borderRadius="lg"
              bg="white"
            >
              <StatLabel fontSize="lg">Total Tests Taken</StatLabel>
              <StatNumber fontSize="3xl">{stats.totalScores}</StatNumber>
              <StatHelpText>{stats.recentTests} in last 24 hours</StatHelpText>
            </Stat>

            <Stat
              p={6}
              shadow="md"
              border="1px"
              borderColor="gray.200"
              borderRadius="lg"
              bg="white"
            >
              <StatLabel fontSize="lg">Average Score</StatLabel>
              <StatNumber fontSize="3xl">{stats.averageScore}%</StatNumber>
              <StatHelpText>
                Range: {stats.lowestScore}% - {stats.highestScore}%
              </StatHelpText>
            </Stat>

            <Stat
              p={6}
              shadow="md"
              border="1px"
              borderColor="gray.200"
              borderRadius="lg"
              bg="white"
            >
              <StatLabel fontSize="lg">Average Memory IQ</StatLabel>
              <StatNumber fontSize="3xl">{stats.averageMemoryIQ}</StatNumber>
              <StatHelpText>Based on all test results</StatHelpText>
            </Stat>
          </SimpleGrid>

          {/* Search and Filter Section */}
          <Box
            w="full"
            bg="whiteAlpha.100"
            p={4}
            borderRadius="lg"
            backdropFilter="blur(10px)"
          >
            <HStack spacing={4} wrap="wrap">
              <InputGroup flex="2" minW="250px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg="white"
                  _dark={{ bg: "gray.700" }}
                />
              </InputGroup>

              <Select
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                bg="white"
                _dark={{ bg: "gray.700" }}
                maxW="200px"
              >
                <option value="all">All Fields</option>
                <option value="name">Name</option>
                <option value="email">Email</option>
                <option value="country">Country</option>
              </Select>

              <Select
                value={scoreFilter}
                onChange={(e) => setScoreFilter(e.target.value)}
                bg="white"
                _dark={{ bg: "gray.700" }}
                maxW="200px"
              >
                <option value="all">All Scores</option>
                <option value="90+">90% and above</option>
                <option value="70-89">70% - 89%</option>
                <option value="<70">Below 70%</option>
              </Select>
            </HStack>
          </Box>

          {/* Show result count */}
          <Text color="gray.500" alignSelf="flex-start">
            Showing {filteredScores.length} of {scores.length} results
          </Text>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error.message}
            </Alert>
          )}

          {/* Scores Grid */}
          <SimpleGrid columns={{ base: 1, lg: 1 }} spacing={6} w="full">
            {currentItems.length > 0 ? (
              currentItems.map((score, index) => (
                <MotionBox
                  key={score.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Box position="relative">
                    {/* <SimpleGrid columns={2}> */}
                      <Box w="90%" h="100%">
                        <ScoreCard
                          score={score}
                          onViewCertificate={(score) => {
                            setPlayerResult(score);
                            setIsModalOpen(true);
                          }}
                        />
                      </Box>
                      <Box w="10%" h="100%">
                        <IconButton
                          aria-label="Delete score"
                          icon={<DeleteIcon />}
                          position="absolute"
                          top={4}
                          right={4}
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDeleteClick(score)}
                          _hover={{
                            bg: "red.100",
                            color: "red.600",
                          }}
                        />
                      </Box>
                    {/* </SimpleGrid> */}
                  </Box>
                </MotionBox>
              ))
            ) : (
              <Box
                textAlign="center"
                p={8}
                bg="whiteAlpha.100"
                borderRadius="xl"
                backdropFilter="blur(10px)"
                w="full"
              >
                <Text color="gray.200" fontSize="lg">
                  No results found matching your search criteria.
                </Text>
              </Box>
            )}
          </SimpleGrid>

          {/* Empty State */}
          {!isLoading && scores.length === 0 && (
            <Box
              textAlign="center"
              p={8}
              bg="whiteAlpha.100"
              borderRadius="xl"
              backdropFilter="blur(10px)"
              w="full"
            >
              <Text color="gray.200" fontSize="lg">
                You haven't taken any Memory IQ tests yet. Start a test to see
                your scores here!
              </Text>
            </Box>
          )}
        </VStack>
        {/* Pagination */}
        {filteredScores.length > itemsPerPage && (
          <Box
            mt={8}
            p={4}
            borderRadius="lg"
            bg="whiteAlpha.100"
            backdropFilter="blur(10px)"
          >
            <ButtonGroup spacing={2} variant="outline">
              {Array.from({
                length: Math.ceil(filteredScores.length / itemsPerPage),
              }).map((_, index) => (
                <Button
                  key={index}
                  onClick={() => paginate(index + 1)}
                  colorScheme="blue"
                  size="md"
                  variant={
                    currentItems[0] &&
                    currentItems[0] === filteredScores[index * itemsPerPage]
                      ? "solid"
                      : "outline"
                  }
                  _hover={{
                    transform: "translateY(-2px)",
                    shadow: "lg",
                  }}
                  transition="all 0.2s"
                >
                  {index + 1}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          isOpen={!!scoreToDelete}
          leastDestructiveRef={cancelRef}
          onClose={() => setScoreToDelete(null)}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Score
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to delete this score? This action cannot
                be undone.
                <Box mt={4} p={4} bg="gray.50" borderRadius="md">
                  <Text>
                    <strong>User:</strong> {scoreToDelete?.firstName}{" "}
                    {scoreToDelete?.lastName}
                  </Text>
                  <Text>
                    <strong>Score:</strong> {scoreToDelete?.score}%
                  </Text>
                  <Text>
                    <strong>Memory IQ:</strong> {scoreToDelete?.memoryIQScore}
                  </Text>
                </Box>
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={() => setScoreToDelete(null)}>
                  Cancel
                </Button>
                <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        <CertificateModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          score={playerResult ?? ({} as Score)}
        />
      </Container>
    </ContentCard>
  );
};
